"use client";

import { useState, useCallback, useEffect } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { Alert, AlertDescription, Button, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useSplitTreatments } from "@zapier/client";
import { pathMatchesShowForPaths } from "../../utils/matchPathToShowForPaths";
import styles from "./WhatsNewAnnouncement.module.css";

const DISMISSED_KEY_PREFIX = "whats-new-popover-dismissed";

/** Simple djb2 hash for a short, stable key from the message. */
function hashMessage(string_: string): string {
  let hash = 5381;
  for (let index = 0; index < string_.length; index++) {
    hash = (hash * 33) ^ (string_.codePointAt(index) ?? 0);
  }
  return (hash >>> 0).toString(16);
}

/**
 * Config from Split whats_new_popover_enabled flag.
 * showForPaths: exact paths or regex patterns (e.g. "^/app/assets/folders/[a-f0-9-]+$" for all folders).
 */
function WhatsNewAnnouncementComponent() {
  const pathname = usePathname() ?? "";
  const { isShowWhatsNewAnnouncementEnabled, showWhatsNewAnnouncementConfig } =
    useSplitTreatments();

  const message = showWhatsNewAnnouncementConfig?.message?.trim();
  const dismissedKey = message
    ? `${DISMISSED_KEY_PREFIX}-${hashMessage(message)}`
    : DISMISSED_KEY_PREFIX;

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (globalThis.window === undefined || !message) return;
    setIsVisible(!localStorage.getItem(dismissedKey));
  }, [dismissedKey, message]);

  const handleDismiss = useCallback(() => {
    if (globalThis.window !== undefined) {
      localStorage.setItem(dismissedKey, "true");
    }
    setIsVisible(false);
  }, [dismissedKey]);

  const handleCtaClick = useCallback(() => {
    const url = showWhatsNewAnnouncementConfig?.buttonUrl?.trim();
    if (url) {
      if (url.startsWith("#")) {
        globalThis.history.pushState(undefined, "", url);
        globalThis.dispatchEvent(new PopStateEvent("popstate"));
      } else {
        const ALLOWED_PROTOCOLS = ["http:", "https:"];
        try {
          const parsed = new URL(url, globalThis.location?.origin);
          if (ALLOWED_PROTOCOLS.includes(parsed.protocol)) {
            window.open(url, "_blank", "noopener,noreferrer");
          }
        } catch {
          // Invalid URL — do nothing
        }
      }
    }
    handleDismiss();
  }, [showWhatsNewAnnouncementConfig?.buttonUrl, handleDismiss]);

  if (!isShowWhatsNewAnnouncementEnabled || !message) {
    return;
  }

  const pathMatches = pathMatchesShowForPaths(
    pathname,
    showWhatsNewAnnouncementConfig?.showForPaths ?? undefined,
  );

  if (!pathMatches || !isVisible) {
    return;
  }

  const buttonText =
    showWhatsNewAnnouncementConfig?.buttonText?.trim() || "See what's new";
  const buttonUrl = showWhatsNewAnnouncementConfig?.buttonUrl?.trim();
  const imageUrl = showWhatsNewAnnouncementConfig?.imageUrl?.trim();
  const imageAltText = showWhatsNewAnnouncementConfig?.imageAltText ?? "";

  return (
    <div className={styles.whatsNewWrapper}>
      <Alert onDismiss={handleDismiss} status="info" variant="toast">
        <AlertDescription maxLines={999}>
          <Text type="Body3">{message}</Text>
          {imageUrl && (
            <div className={styles.imageWrapper}>
              <img src={imageUrl} alt={imageAltText} />
            </div>
          )}
          {buttonUrl && (
            <div className={styles.buttonWrapper}>
              <Button
                variant="primary"
                size="small"
                onPress={handleCtaClick}
                iconPosition="end"
              >
                {buttonText}
                <Icon name="arrowBigRight" size="small" />
              </Button>
            </div>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
}

export const WhatsNewAnnouncement = dynamic(
  () => Promise.resolve(WhatsNewAnnouncementComponent),
  { ssr: false },
);
